import { DateTime } from "luxon";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Step } from "react-joyride";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { DraftModeContext } from "../../../contexts/DraftModeContext";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useWeekIndex } from "../../../contexts/WeekIndexProvider";
import { useIsMobile } from "../../../hooks/useMobile";
import type { AnnieWalkthrough, AnnieWalkthroughStep, AnnieWalkthroughSurface } from "../../../utils/annieStream";
import { dismissRevealedAnchors, openerCanReveal, presentAnchors } from "./walkthroughAnchors";
import { buildPath, isOnRoute, isRunnableHere, STAFFING_WEEK_ROUTE } from "./walkthroughRoute";
import { eagerSteps, joyrideSteps, survivingSteps } from "./walkthroughSteps";

/** Typed against the surface union rather than spelled inline, so dropping the member fails the build here. */
const RUNNABLE_SURFACE: AnnieWalkthroughSurface = "desktop";

/** Every reason a tour that was offered does not actually run. Each one is tracked AND said out loud. */
type RefusalReason = "platform" | "unsaved_changes" | "unresolved_route" | "too_few_visible_steps" | "error";

const REFUSAL_MESSAGE_KEYS: Record<RefusalReason, string> = {
	platform: "annie.walkthrough.refused.platform",
	unsaved_changes: "annie.walkthrough.refused.unsaved_changes",
	unresolved_route: "annie.walkthrough.refused.unresolved_route",
	too_few_visible_steps: "annie.walkthrough.refused.too_few_steps",
	error: "annie.walkthrough.refused.error",
};

type LaunchState = {
	/** True once the tour has navigated, snapshotted its anchors and cleared the step floor. */
	isRunning: boolean;
	/** The joyride steps that will actually render — eager anchors absent from this page already filtered out. */
	steps: Step[];
	/** End the tour from outside joyride (the Escape key), without going through a joyride event. */
	stop: () => void;
};

/**
 * Decides whether an offered walkthrough runs, gets it into the state it needs, and reports what happened.
 *
 * Split out of `WalkthroughController` because none of this is rendering: it navigates, polls the DOM,
 * applies the step floor, and emits the analytics that make a tour measurable. The component is left with
 * the joyride element and its event handler.
 */
export const useWalkthroughLaunch = (walkthrough: AnnieWalkthrough | null, onFinish: () => void): LaunchState => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isMobile } = useIsMobile();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const hasUnsavedChanges = useContext(DraftModeContext)?.hasUnsavedChanges === true;
	const { chosenDepartment } = useChosenDepartment();
	const { getWeekIndexByDate, timezone } = useHospitalData();
	const { setWeekIndex } = useWeekIndex();
	const [isRunning, setIsRunning] = useState<boolean>(false);
	const [visibleAnchors, setVisibleAnchors] = useState<Set<string> | null>(null);
	// The authored steps this launch runs: a step that explains a move is dropped when the tour runs in place.
	const [launchSteps, setLaunchSteps] = useState<AnnieWalkthroughStep[]>([]);
	// Openers that are on the page but cannot open what follows them — every marked trigger inside them is
	// disabled. Measured at launch alongside the anchor scan, because it is a property of this department
	// and this render, not of the authored asset.
	const [blockedOpeners, setBlockedOpeners] = useState<ReadonlySet<string>>(new Set());
	// `onFinish` is typically an inline arrow, so without this the tour re-navigates and jumps back to
	// step 1 on any parent re-render.
	const launchedRef = useRef<AnnieWalkthrough | null>(null);
	// UNMOUNT specifically, not effect cleanup. The anchor scan outlives the effect run that started it on
	// purpose (see the staleness note below), so cancelling on cleanup would abort the very launch this is
	// protecting. Only the host going away means nobody is left to navigate, warn, or hold the state.
	const unmountedRef = useRef(false);
	useEffect(() => {
		// Cleared on every MOUNT, not just set on unmount. A latch that is only ever set to true is wrong the
		// moment this component mounts twice: StrictMode runs mount → cleanup → mount in development, so the
		// flag was already true before the first tour was ever offered and EVERY scan returned `abandoned` —
		// silently, by design, since an abandoned scan has nobody left to tell. No walkthrough could run at
		// all. The same latch would stick in production after any genuine remount (Annie's availability
		// flipping, the ErrorBoundary resetting, a rotate to mobile and back).
		unmountedRef.current = false;
		return () => {
			unmountedRef.current = true;
		};
	}, []);

	const steps = useMemo<Step[]>(
		() =>
			walkthrough === null || visibleAnchors === null
				? []
				: joyrideSteps(survivingSteps(launchSteps, visibleAnchors, blockedOpeners), (bodyKey, bodyParams) =>
						bodyParams === null ? t(bodyKey) : t(bodyKey, bodyParams),
					),
		[walkthrough, launchSteps, visibleAnchors, blockedOpeners, t],
	);

	// Tracked AND said out loud: the chip moved the user's attention, so silence reads as a broken button.
	const refuse = useCallback(
		(topicId: string, reason: RefusalReason, detail?: Record<string, number>) => {
			track("annie_walkthrough_refused", { topic_id: topicId, reason, ...detail });
			showSnackbar(t(REFUSAL_MESSAGE_KEYS[reason]), "info");
			onFinish();
		},
		[track, showSnackbar, t, onFinish],
	);

	// The weekly grid renders `weekIndex`, not the URL, and its week arrows move only the address bar
	// (`replaceState`, behind the router). So the router can already hold the date a tour navigates to, and
	// then the navigate changes nothing and the grid stays on the past week the user paged to. Setting the
	// week directly, as the date picker does, lands it whatever the router thinks.
	const showWeek = useCallback(
		(topicId: string, dateString: string) => {
			// Parsed IN the department's zone: a browser-midnight date can fall in the previous week.
			const weekIndex = getWeekIndexByDate(DateTime.fromISO(dateString, { zone: timezone }));
			if (weekIndex === -1) {
				// Not refused here: the anchor scan still runs and refuses out loud if the grid is wrong.
				trackError("annie_walkthrough_week_unresolved", { topic_id: topicId, date: dateString });
				return;
			}
			setWeekIndex(weekIndex);
		},
		[getWeekIndexByDate, timezone, setWeekIndex, trackError],
	);

	const stop = useCallback(() => {
		setIsRunning(false);
		// The tour can carry the user INTO a menu, drawer or modal, so finishing has to carry them back
		// out — otherwise "Done" leaves them stranded in a half-filled panel they never meant to open,
		// with the tour gone and no explanation.
		//
		// Not awaited: the tour is already over and a control that refuses to close must not hold up the
		// panel coming back. Tracked rather than swallowed, though — `anchorSelector` throws on a malformed
		// name, and a dismissal that silently failed would leave exactly the stranded modal this exists to
		// prevent, with nothing anywhere saying so.
		const topicId = walkthrough?.topicId;
		//
		// INNERMOST FIRST, which is why the authored order is reversed. `dismissRevealedAnchors` acts on the
		// first anchor it finds visible and unwinds one layer per pass, and surfaces are revealed outermost
		// first — so the authored order asks the OUTER surface to close first. Where they nest that is wrong
		// twice over: closing the Staffing drawer unmounts the composer inside it, and the single global
		// dismiss registration belongs to the innermost surface, so the outer pass would fire the inner
		// surface's handler. It happened to come out right before only because the retry pass caught it.
		const deferredAnchors =
			walkthrough === null
				? []
				: walkthrough.steps
						.filter((step) => step.reveal === "interaction")
						.map((step) => step.anchor)
						.reverse();
		dismissRevealedAnchors(deferredAnchors).catch((error) => trackError("annie_walkthrough_dismiss_failed", { topic_id: topicId, message: String(error) }));
		onFinish();
	}, [walkthrough, onFinish, trackError]);

	useEffect(() => {
		if (walkthrough === null) {
			setIsRunning(false);
			setVisibleAnchors(null);
			launchedRef.current = null;
			return;
		}
		if (walkthrough === launchedRef.current) return;
		launchedRef.current = walkthrough;
		// The backend withholds the offer for a mobile surface, so reaching here means the viewport changed
		// after the answer arrived.
		if (isMobile || walkthrough.platform !== RUNNABLE_SURFACE) {
			refuse(walkthrough.topicId, "platform");
			return;
		}
		// `window.location` rather than `useLocation`, deliberately: this must be read ONCE per launch.
		// `navigate` re-runs this effect synchronously, and as a dep it would read the destination on the
		// second pass and report the tour as having stayed put.
		const here = window.location.pathname;
		// Every page this tour can start from: the default one `route` describes, plus any alternates.
		const entryPoints =
			walkthrough.entryPoints !== undefined && walkthrough.entryPoints.length > 0
				? walkthrough.entryPoints
				: [{ id: "", route: walkthrough.route, params: walkthrough.params, isDefault: true }];
		// The page the user is ALREADY on, if the tour knows it — whatever state it happens to show.
		const onEntryPoint = entryPoints.find((entry) => entry.route !== null && isOnRoute(entry.route, chosenDepartment.department_id, here));
		// Exact-match params belong to the tour's own `route`/`params` — a data-resolved param is only ever
		// authored on the single launch route, never on an alternate surface — so they bind the default entry
		// point and nothing else.
		const alreadyThere =
			onEntryPoint?.route != null &&
			isRunnableHere(onEntryPoint.route, onEntryPoint.params, onEntryPoint.isDefault ? walkthrough.exactParams : [], chosenDepartment.department_id, here);
		// When we do have to move, prefer the page the user chose over the default one. Someone on a PAST
		// day of the Day view wants today's Day view, not to be thrown onto the week grid — the page was
		// their decision, only the date was unusable.
		const moveTo = onEntryPoint ?? entryPoints.find((entry) => entry.isDefault) ?? entryPoints[0];
		const destination = moveTo?.route == null ? null : buildPath(moveTo.route, moveTo.params, chosenDepartment.department_id);
		// Before navigating, not at render: by the time the tour renders, the work is already gone. Not a
		// reason to refuse when we are not navigating — staying put discards nothing.
		if (walkthrough.route !== null && !alreadyThere && hasUnsavedChanges) {
			refuse(walkthrough.topicId, "unsaved_changes");
			return;
		}
		if (walkthrough.route !== null && destination === null) {
			refuse(walkthrough.topicId, "unresolved_route");
			return;
		}
		if (destination !== null && !alreadyThere) {
			navigate(destination);
			if (moveTo?.route === STAFFING_WEEK_ROUTE) showWeek(walkthrough.topicId, moveTo.params.dateString);
		}

		// A step that explains the move ("so I've brought you here") only makes sense if there WAS one.
		const navigated = destination !== null && !alreadyThere;
		const stepsForLaunch = navigated ? walkthrough.steps : walkthrough.steps.filter((step) => !step.onlyAfterNavigation);
		// Decide the real length BEFORE the first tooltip: the server floor was applied to the authored
		// steps, which can't know a control is hidden for this department, so a 3-step offer could deliver
		// 2 and stop abruptly.
		const eager = eagerSteps(stepsForLaunch);
		void (async () => {
			try {
				// EAGER anchors only. A deferred anchor is known-absent at scan time — its control is behind a click
				// nobody has made yet — so including it would never let the scan complete early, burning the full 3s
				// deadline on every launch and restarting the ANCHOR_GRACE_MS window for something that cannot arrive.
				const scan = await presentAnchors(
					eager.map((step) => step.anchor),
					// Nothing to wait for when we did not navigate — and passing the default entry point's
					// path here would be wrong, not merely redundant: on an ALTERNATE entry point it is a
					// path we are deliberately not going to, so the scan would time out and refuse a tour
					// whose controls are already on screen. Safe because `alreadyThere` was proved against
					// `window.location.pathname` synchronously in the same tick.
					alreadyThere ? null : destination,
					() => unmountedRef.current,
				);
				// Nobody is left to tell. Returning before the telemetry and the snackbar is the point: the
				// controller is gone, so a refusal would narrate a tour the user can no longer see to a panel
				// that is no longer mounted.
				if (scan.outcome === "abandoned") return;
				// Staleness keyed on the LAUNCH, not the effect run: navigating re-runs this effect immediately,
				// so a cleanup flag would cancel the poll and the re-run would then hit the guard above and
				// return — navigated, never started.
				if (launchedRef.current !== walkthrough) return;
				if (scan.outcome === "route-timeout") {
					// The destination never came up, so any anchors on screen belong to somewhere else.
					refuse(walkthrough.topicId, "unresolved_route");
					return;
				}
				const present = scan.anchors;
				// Measured against EAGER-visible only, which is the honest "we proved these are on screen" count —
				// the same thing the server floor counts. Letting a deferred step help clear it would launch a tour
				// on a control the user may never reveal, which is exactly the abrupt stop the floor exists for.
				// A step that only explains the move is not tour content either (the server floor skips it too):
				// counting it let the call-off tour run as "brought you here" → Staffing, with no dialog at all.
				const contentAnchors = new Set(eager.filter((step) => !step.onlyAfterNavigation && present.has(step.anchor)).map((step) => step.anchor));
				if (contentAnchors.size < walkthrough.minSteps) {
					refuse(walkthrough.topicId, "too_few_visible_steps", { authored: eager.length, visible: contentAnchors.size });
					return;
				}
				// An opener whose triggers are all disabled keeps its own step and loses its deferred tail.
				const blocked = new Set(eager.filter((step) => present.has(step.anchor) && !openerCanReveal(step.anchor)).map((step) => step.anchor));
				setBlockedOpeners(blocked);
				setLaunchSteps(stepsForLaunch);
				setVisibleAnchors(present);
				// The same list the memo will render, so `steps` is what the user is actually promised — a deferred
				// step whose opener was dropped is not in it.
				const rendered = survivingSteps(stepsForLaunch, present, blocked);
				track("annie_walkthrough_started", {
					topic_id: walkthrough.topicId,
					steps: rendered.length,
					// A persistent gap is an authoring fix, not a runtime one. Eager only: a deferred step was never
					// looked for, so counting it here would report every tour as permanently broken.
					dropped: eager.length - present.size,
					// Rendered but unproven — how many of `steps` depend on the user opening something.
					deferred: rendered.length - present.size,
				});
				setIsRunning(true);
			} catch (error) {
				// A throw in here used to be an unhandled rejection and nothing else: no refusal, no snackbar,
				// no `onFinish` — the chip simply did nothing and the offer stayed on screen with no way to
				// find out why. Everything above is fallible (a malformed anchor once made `querySelector`
				// throw rather than return null), so the scan needs the same "say so out loud" contract as
				// every other way a tour declines to run.
				trackError("annie_walkthrough_launch_failed", {
					topic_id: walkthrough.topicId,
					error_message: error instanceof Error ? error.message : String(error),
				});
				// Staleness checked here too, not just on the success path above. `refuse` ends with Layout's
				// shared `onFinish`, which clears whatever walkthrough is active — so a tour the user already
				// replaced, throwing on its way out, would take the NEW one down with it and snackbar about a
				// tour they had moved on from. The failure is still worth the telemetry above either way.
				if (!unmountedRef.current && launchedRef.current === walkthrough) refuse(walkthrough.topicId, "error");
			}
		})();
	}, [walkthrough, isMobile, navigate, refuse, track, trackError, hasUnsavedChanges, chosenDepartment.department_id, showWeek]);

	return { isRunning, steps, stop };
};
